import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

const RevokePersonalAccessTokenMutation = graphql(/* GraphQL */ `
  mutation revokePersonalAccessToken($input: RevokePersonalAccessTokenInput!) {
    revokePersonalAccessToken(input: $input) {
      ... on RevokePersonalAccessTokenSuccess {
        active
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

type RevokeTokenResult = { success: true } | { success: false; error: string }

type UseRevokePersonalAccessTokenResult = {
  revokeToken: (accessToken: string) => Promise<RevokeTokenResult>
  isLoading: boolean
}

export const useRevokePersonalAccessToken =
  (): UseRevokePersonalAccessTokenResult => {
    const [mutate, { loading }] = useMutation(RevokePersonalAccessTokenMutation)

    const revokeToken = async (
      accessToken: string,
    ): Promise<RevokeTokenResult> => {
      try {
        const result = await mutate({
          variables: { input: { accessToken } },
        })

        if (result.error) {
          return { success: false, error: result.error.message }
        }

        const response = result.data?.revokePersonalAccessToken

        if (response && 'active' in response && response.active === false) {
          return { success: true }
        }

        if (response && 'message' in response) {
          return { success: false, error: response.message }
        }

        return {
          success: false,
          error: 'Failed to revoke token. Please try again.',
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred. Please try again.'

        return { success: false, error: message }
      }
    }

    return {
      revokeToken,
      isLoading: loading,
    }
  }
